/**
 * Circular-junction turntable: drive to the hub, turn with the disk, drive out.
 *
 * Stubs in the graph run tip→rim→hub, so the OHT rolls continuously to the
 * centre. There it parks on the diameter rail, the disk sweeps it round to the
 * exit axis, and it rolls out again — no teleporting across the circle.
 */
import { LayoutElement } from '../types';
import {
  chooseNextEdge,
  chooseNextEdgeTowardStop,
  pointAtDistance,
  type TrackEdge,
  type TrackGraph,
} from './simulationGraph';
import {
  getCircleJunctionActivePortIndices,
  getCircleJunctionConnectedPortIndices,
  getCircleJunctionMaxPorts,
  getCircleJunctionRingRadius,
  getCircleJunctionSpokesLocal,
  getCircleJunctionThroughBarAngleDeg,
  isJunctionRailType,
  resolveRailType,
} from './topology';
import {
  junctionTurntableStore,
  shortestAngleDeltaDeg,
  turntableAnglesAligned,
  type JunctionTurntableAnim,
} from '../store/junctionTurntableStore';
import { playbackDelayStore } from '../store/playbackDelayStore';
import type { VisualizationEvent } from '../types/simulationPlayback';
import { buildTrackGraph } from './simulationGraph';
import { getExportableTrackEdges } from './simulationTrackNetworkView';

/** Base duration for a 90° disk sweep (simulation ms at 1×). */
export const TURNTABLE_ROTATE_MS_PER_90 = 1500;
/**
 * Fallback minimum hub hold (simulation ms at 1×) when a junction has no
 * `junctionWaitSec` set. Prefer {@link DEFAULT_JUNCTION_WAIT_SEC} via
 * {@link getJunctionWaitSec}.
 */
export const TURNTABLE_MIN_HOLD_MS = 400;
/** Default dedicated OHT wait at a circle junction (seconds, sim time at 1×). */
export const DEFAULT_JUNCTION_WAIT_SEC = 3;
/** Fraction of the stub left to run when the disk starts pre-aligning. */
const APPROACH_FRAC = 0.8;

export type TurntablePose = {
  ohtId: string;
  junctionId: string;
  x: number;
  y: number;
  angle: number;
};

export type TurntableComplete = {
  id: string;
  updates: Partial<LayoutElement>;
  ohtId: string;
  exitEdgeIndex: number;
  exitDir: 1 | -1;
  /** Start distance on the exit stub (at the hub, heading out). */
  exitDist: number;
  landAt: { x: number; y: number; angle: number };
};

export type TurntableTickResult = {
  completed: TurntableComplete[];
  rotating: TurntablePose[];
  /** OHT ids riding a disk this frame (must not advance along stubs). */
  holdAtHubOhtIds: Set<string>;
};

export function getTurntableSettledAngleDeg(
  el: LayoutElement,
  rails: readonly LayoutElement[] = []
): number {
  const raw = Number(el.customProperties?.turntableAngleDeg);
  if (Number.isFinite(raw)) return raw;

  const maxPorts = getCircleJunctionMaxPorts(el);
  const spokes = getCircleJunctionSpokesLocal(
    el.dimensions.width,
    el.dimensions.height,
    maxPorts
  );

  if (rails.length > 0) {
    return getCircleJunctionThroughBarAngleDeg(el, rails);
  }

  const conns = el.customProperties?.connections as Array<{ fromPortId?: string; toPortId?: string }> | undefined;
  if (Array.isArray(conns) && conns.length > 0 && conns[0]?.fromPortId) {
    const match = /^port-(\d+)$/.exec(conns[0].fromPortId);
    if (match) {
      const pIdx = Number(match[1]);
      if (spokes[pIdx]) return spokes[pIdx].angle;
    }
  }

  const activePortIds = el.customProperties?.activePortIds as string[] | undefined;
  if (Array.isArray(activePortIds) && activePortIds.length > 0 && typeof activePortIds[0] === 'string') {
    const match = /^port-(\d+)$/.exec(activePortIds[0]);
    if (match) {
      const pIdx = Number(match[1]);
      if (spokes[pIdx]) return spokes[pIdx].angle;
    }
  }

  return spokes[0]?.angle ?? 0;
}

/** Layout-configured diametric bar (explicit angle or connected through-axis). */
export function getConfiguredTurntableBarAngleDeg(
  el: LayoutElement,
  rails: readonly LayoutElement[] = []
): number {
  const raw = el.customProperties?.turntableAngleDeg;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw !== '' && Number.isFinite(Number(raw))) {
    return Number(raw);
  }
  return getCircleJunctionThroughBarAngleDeg(el, rails);
}

/** Exactly two rails on opposing spokes — fixed bidirectional through connection. */
export function isBidirectionalThroughJunction(
  el: LayoutElement,
  rails: readonly LayoutElement[] = []
): boolean {
  const connected = getCircleJunctionConnectedPortIndices(el, rails);
  if (connected.size !== 2) return false;
  const [a, b] = [...connected].sort((x, y) => x - y);
  const a1 = getTurntablePortAngleDeg(el, a!);
  const a2 = getTurntablePortAngleDeg(el, b!);
  const diff = Math.abs(shortestAngleDeltaDeg(a1, a2));
  return Math.abs(diff - 180) < 8;
}

export function shouldLockTurntableBarForPorts(
  el: LayoutElement,
  fromPort: number,
  toPort: number,
  rails: readonly LayoutElement[] = []
): boolean {
  if (!isBidirectionalThroughJunction(el, rails)) return false;
  const connected = getCircleJunctionConnectedPortIndices(el, rails);
  return connected.has(fromPort) && connected.has(toPort);
}

/** Opposite spoke on the same diametric rail (among active/connected ports). */
export function resolveTurntableOppositePort(
  el: LayoutElement,
  portIndex: number,
  activePorts?: ReadonlySet<number>
): number | null {
  const spokes = getCircleJunctionSpokesLocal(
    el.dimensions.width,
    el.dimensions.height,
    getCircleJunctionMaxPorts(el)
  );
  const fromAngle = spokes[portIndex]?.angle;
  if (fromAngle == null) return null;
  const restrict = activePorts && activePorts.size > 0 ? activePorts : null;
  let best: number | null = null;
  let bestErr = Infinity;
  spokes.forEach((spoke, index) => {
    if (index === portIndex) return;
    if (restrict && !restrict.has(index)) return;
    const err = Math.abs(Math.abs(shortestAngleDeltaDeg(fromAngle, spoke.angle)) - 180);
    if (err < bestErr) {
      bestErr = err;
      best = index;
    }
  });
  return bestErr <= 12 ? best : null;
}

/** Continuous through-line (two connected rails) — no hub rotation. */
export function isTurntableStraightThroughMove(
  el: LayoutElement,
  fromPort: number,
  toPort: number,
  rails: readonly LayoutElement[] = []
): boolean {
  return shouldLockTurntableBarForPorts(el, fromPort, toPort, rails);
}

/** Master flag: when false, OHTs hop the hub without any disk animation. */
export function isTurntableAnimationEnabled(el: LayoutElement): boolean {
  const raw = el.customProperties?.animateTurntable;
  return !(raw === false || raw === 'false' || raw === 0 || raw === '0');
}

/** Port heading in the element local frame (deg). */
export function getTurntablePortAngleDeg(el: LayoutElement, portIndex: number): number {
  const spokes = getCircleJunctionSpokesLocal(
    el.dimensions.width,
    el.dimensions.height,
    getCircleJunctionMaxPorts(el)
  );
  return spokes[portIndex]?.angle ?? 0;
}

/** Port heading in world deg (local + element.rotation). */
export function getTurntablePortWorldAngleDeg(el: LayoutElement, portIndex: number): number {
  return getTurntablePortAngleDeg(el, portIndex) + (el.rotation || 0);
}

export function getTurntableHubWorld(el: LayoutElement): { x: number; y: number } {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const lx = w / 2;
  const ly = h / 2;
  return {
    x: el.position.x + lx * cos - ly * sin,
    y: el.position.y + lx * sin + ly * cos,
  };
}

/** Half-length of the visual diametric rail (matches the 2D/3D renderers). */
export function getTurntableRailHalfLength(el: LayoutElement): number {
  const ringR = getCircleJunctionRingRadius(el.dimensions.width, el.dimensions.height);
  return ringR;
}

/**
 * Tip→rim distance along a turntable stub (points are tip, rim, hub).
 * Falls back to ~half length for older two-point tip→hub edges.
 */
export function getTurntableTipToRimDist(edge: TrackEdge): number {
  if (edge.points.length >= 3) {
    const a = edge.points[0];
    const b = edge.points[1];
    return Math.hypot(b.x - a.x, b.y - a.y);
  }
  return edge.length * 0.55;
}

export function turntableDurationMs(fromAngleDeg: number, toAngleDeg: number): number {
  const abs = Math.abs(shortestAngleDeltaDeg(fromAngleDeg, toAngleDeg));
  if (abs < 0.5) return 1;
  return Math.max(300, (abs / 90) * TURNTABLE_ROTATE_MS_PER_90);
}

function firstNonNegativeSec(...values: unknown[]): number | null {
  for (const value of values) {
    const raw = Number(value);
    if (Number.isFinite(raw) && raw >= 0) return raw;
  }
  return null;
}

/**
 * Dedicated OHT wait before the turntable animation starts.
 * Prefers API `waitSec` / `waitS` when present, then the layout
 * `junctionWaitSec`, then {@link DEFAULT_JUNCTION_WAIT_SEC}.
 */
export function getJunctionWaitSec(el: LayoutElement): number {
  const props = el.customProperties ?? {};
  return (
    firstNonNegativeSec(props.waitSec, props.waitS, props.junctionWaitSec) ??
    DEFAULT_JUNCTION_WAIT_SEC
  );
}

/** Dedicated OHT wait at a circle junction (simulation ms at 1×). */
export function getJunctionWaitMs(el: LayoutElement): number {
  return Math.max(0, getJunctionWaitSec(el) * 1000);
}

/**
 * Snap a disk angle onto the nearest active port axis (either end of the
 * diameter). Prevents the bar from freezing at a diagonal mid-sweep.
 */
export function snapTurntableAngleToNearestPort(
  el: LayoutElement,
  angleDeg: number,
  activePorts?: ReadonlySet<number>
): number {
  const spokes = getCircleJunctionSpokesLocal(
    el.dimensions.width,
    el.dimensions.height,
    getCircleJunctionMaxPorts(el)
  );
  const restrict = activePorts && activePorts.size > 0 ? activePorts : null;
  let best = angleDeg;
  let bestErr = Infinity;
  spokes.forEach((spoke, index) => {
    if (restrict && !restrict.has(index)) return;
    for (const cand of [spoke.angle, spoke.angle + 180]) {
      const err = Math.abs(shortestAngleDeltaDeg(angleDeg, cand));
      if (err < bestErr) {
        bestErr = err;
        best = cand;
      }
    }
  });
  return best;
}

/**
 * Phase duration in simulation milliseconds.
 *
 * `waiting` uses the dedicated `waitSec` window (`minHoldMs`). The following
 * boarded align/rotate uses the geometric sweep so the disk finishes before
 * the OHT is released. Wall time scales with sim-clock × playbackSpeed.
 */
export function phaseDurationMs(anim: JunctionTurntableAnim): number {
  if (anim.phase === 'waiting') {
    const waitMs =
      typeof anim.minHoldMs === 'number' && Number.isFinite(anim.minHoldMs)
        ? anim.minHoldMs
        : 0;
    return Math.max(1, waitMs);
  }
  const raw =
    anim.phase === 'aligning'
      ? turntableDurationMs(anim.alignFromDeg, anim.alignToDeg)
      : turntableDurationMs(0, anim.deltaDeg);
  if (!anim.boarded) return raw;
  // Wait-then-rotate holds animate via boarded `aligning` with deltaDeg≈0.
  // Graph-sim rides still use a short align then a separate `rotating` sweep —
  // only those rotate/align-only windows need the minimum hold.
  if (anim.phase === 'rotating' || Math.abs(anim.deltaDeg) < 0.5) {
    return Math.max(TURNTABLE_MIN_HOLD_MS, raw);
  }
  return raw;
}

/**
 * Plan the disk motion for an entry→exit port pair.
 *
 * The OHT drives in heading `fromPort + 180` and must leave heading `toPort`,
 * so the disk (and the vehicle on it) turns by exactly that signed difference.
 * `alignTo` is the representative of the entry axis nearest the settled angle,
 * which keeps the pre-alignment sweep short.
 */
export function planTurntableMove(
  el: LayoutElement,
  fromPort: number,
  toPort: number,
  rails: readonly LayoutElement[] = []
): {
  alignFromDeg: number;
  alignToDeg: number;
  headingInDeg: number;
  deltaDeg: number;
} {
  const fromAngle = getTurntablePortAngleDeg(el, fromPort);
  const toAngle = getTurntablePortAngleDeg(el, toPort);
  const headingIn = fromAngle + 180;
  const deltaDeg = shortestAngleDeltaDeg(headingIn, toAngle);

  if (shouldLockTurntableBarForPorts(el, fromPort, toPort, rails)) {
    const fixedBar = getConfiguredTurntableBarAngleDeg(el, rails);
    return {
      alignFromDeg: fixedBar,
      alignToDeg: fixedBar,
      headingInDeg: headingIn,
      deltaDeg: 0,
    };
  }

  const settled = getTurntableSettledAngleDeg(el, rails);
  const candA = fromAngle;
  const candB = fromAngle + 180;
  const alignToDeg =
    Math.abs(shortestAngleDeltaDeg(settled, candA)) <=
    Math.abs(shortestAngleDeltaDeg(settled, candB))
      ? candA
      : candB;

  return { alignFromDeg: settled, alignToDeg, headingInDeg: headingIn, deltaDeg };
}

/**
 * World pose of the OHT while it rides the disk: pinned to the hub centre,
 * heading sweeping from the entry direction to the exit direction.
 */
export function getTurntableRidePose(
  el: LayoutElement,
  anim: JunctionTurntableAnim
): { x: number; y: number; angle: number } {
  const hub = getTurntableHubWorld(el);
  if (anim.phase === 'waiting') {
    return {
      x: hub.x,
      y: hub.y,
      angle: anim.headingInDeg + (el.rotation || 0),
    };
  }
  const raw = anim.boarded
    ? Math.max(0, Math.min(1, anim.progress))
    : anim.phase === 'rotating'
      ? Math.max(0, Math.min(1, anim.progress))
      : 0;
  const t = raw * raw * (3 - 2 * raw);
  const headingDelta =
    typeof anim.headingDeltaDeg === 'number' && Number.isFinite(anim.headingDeltaDeg)
      ? anim.headingDeltaDeg
      : anim.phase === 'rotating'
        ? anim.deltaDeg
        : 0;
  return {
    x: hub.x,
    y: hub.y,
    angle: anim.headingInDeg + headingDelta * t + (el.rotation || 0),
  };
}

/** Distance still to run before the OHT reaches the hub end of a stub. */
function distanceToHub(edge: TrackEdge, dist: number, dir: 1 | -1): number {
  return dir > 0 ? Math.max(0, edge.length - dist) : Math.max(0, dist);
}

/** Hub node id of a turntable stub for an OHT travelling in `dir`. */
function hubNodeOf(edge: TrackEdge, dir: 1 | -1): number {
  return dir > 0 ? edge.b : edge.a;
}

/**
 * Predict the exit the OHT will take at the hub. Mirrors the sim loop's own
 * choice — target stop first, straightest heading as fallback — so the disk
 * pre-spins toward the port the vehicle will actually leave by.
 */
function resolveExit(
  graph: TrackGraph,
  edge: TrackEdge,
  oht: {
    edgeIndex: number;
    dir: 1 | -1;
    targetStop?: { railId: string; distance: number } | null;
  }
): { edgeIndex: number; dir: 1 | -1; edge: TrackEdge } | null {
  const heading =
    oht.dir > 0
      ? pointAtDistance(edge, edge.length).angle
      : pointAtDistance(edge, 0).angle + 180;
  const node = hubNodeOf(edge, oht.dir);
  const towardStop = oht.targetStop
    ? chooseNextEdgeTowardStop(
        graph,
        node,
        oht.edgeIndex,
        heading,
        oht.targetStop.railId,
        oht.targetStop.distance
      )
    : null;
  const choice = towardStop ?? chooseNextEdge(graph, node, heading, oht.edgeIndex);
  if (!choice || choice.edgeIndex === oht.edgeIndex) return null;

  const exitEdge = graph.edges[choice.edgeIndex];
  if (
    !exitEdge ||
    exitEdge.pathKind !== 'turntable' ||
    exitEdge.id !== edge.id ||
    exitEdge.portIndex == null
  ) {
    return null;
  }
  return { edgeIndex: choice.edgeIndex, dir: choice.dir, edge: exitEdge };
}

/**
 * Each sim frame: reserve/advance turntable rides and publish hub poses.
 */
export function tickJunctionTurntables(params: {
  now: number;
  graph: TrackGraph;
  elements: Record<string, LayoutElement> | LayoutElement[];
  ohts: Array<{
    id: string;
    edgeIndex: number;
    dist: number;
    dir: 1 | -1;
    x: number;
    y: number;
    angle: number;
    /** Stop the vehicle is currently heading for, if any. */
    targetStop?: { railId: string; distance: number } | null;
  }>;
}): TurntableTickResult {
  const list = Array.isArray(params.elements)
    ? params.elements
    : Object.values(params.elements);
  const byId = Array.isArray(params.elements)
    ? (Object.fromEntries(list.map((e) => [e.id, e])) as Record<string, LayoutElement>)
    : params.elements;

  const holdAtHubOhtIds = new Set<string>();

  for (const oht of params.ohts) {
    if (junctionTurntableStore.isOhtOnTurntable(oht.id)) {
      holdAtHubOhtIds.add(oht.id);
      continue;
    }
    if (junctionTurntableStore.isOhtReserved(oht.id)) continue;

    const parkedOn = junctionTurntableStore.getParkedJunction(oht.id);
    if (parkedOn) {
      const el = byId[parkedOn];
      if (!el || resolveRailType(el) !== 'junction-circle') {
        junctionTurntableStore.releaseParked(oht.id);
      } else {
        const hub = getTurntableHubWorld(el);
        const away = Math.hypot(oht.x - hub.x, oht.y - hub.y);
        if (away > getTurntableRailHalfLength(el) * 1.6) {
          junctionTurntableStore.releaseParked(oht.id);
        } else {
          continue;
        }
      }
    }

    if (junctionTurntableStore.isOhtCoolingDown(oht.id, params.now)) continue;

    const edge = params.graph.edges[oht.edgeIndex];
    if (!edge || edge.pathKind !== 'turntable' || edge.portIndex == null) continue;
    // Only OHTs heading inward can claim the disk.
    const toHub = distanceToHub(edge, oht.dist, oht.dir);
    if (toHub > edge.length * APPROACH_FRAC) continue;

    const el = byId[edge.id];
    if (!el || !isJunctionRailType(resolveRailType(el))) continue;
    if (!isTurntableAnimationEnabled(el)) continue;
    if (junctionTurntableStore.isAnimating(edge.id)) continue;
    if (junctionTurntableStore.isCoolingDown(edge.id, params.now)) continue;

    const exit = resolveExit(params.graph, edge, oht);
    if (!exit) continue;

    const plan = planTurntableMove(el, edge.portIndex, exit.edge.portIndex!, list);
    // Already lined up and running straight through: let it roll, no reservation.
    if (
      Math.abs(plan.deltaDeg) < 0.5 &&
      turntableAnglesAligned(plan.alignFromDeg, plan.alignToDeg)
    ) {
      continue;
    }

    junctionTurntableStore.start({
      id: edge.id,
      ...plan,
      fromPort: edge.portIndex,
      toPort: exit.edge.portIndex!,
      exitEdgeIndex: exit.edgeIndex,
      exitDir: exit.dir,
      ohtId: oht.id,
      now: params.now,
    });
  }

  const finished = junctionTurntableStore.tick(params.now, phaseDurationMs);

  const completed: TurntableComplete[] = [];
  const rotating: TurntablePose[] = [];

  for (const id of finished) {
    const anim = junctionTurntableStore.getAnim(id);
    const el = byId[id];
    if (!anim || !el) {
      junctionTurntableStore.clear(id);
      continue;
    }

    const hub = getTurntableHubWorld(el);
    const finalAngle = junctionTurntableStore.getFinalAngle(anim);
    const exitEdgeIndex = anim.exitEdgeIndex;
    const exitDir = anim.exitDir;
    const exitEdge = exitEdgeIndex >= 0 ? params.graph.edges[exitEdgeIndex] : undefined;
    const exitDist = exitEdge ? (exitDir > 0 ? 0 : exitEdge.length) : 0;

    junctionTurntableStore.markSettled(id, params.now);
    const lockBar = shouldLockTurntableBarForPorts(
      el,
      anim.fromPort,
      anim.toPort,
      list
    );
    const settledAngle = lockBar
      ? getConfiguredTurntableBarAngleDeg(el, list)
      : finalAngle;
    completed.push({
      id,
      updates: lockBar
        ? {}
        : {
        customProperties: {
          ...el.customProperties,
          turntableAngleDeg: settledAngle,
        },
      },
      ohtId: anim.ohtId,
      exitEdgeIndex,
      exitDir,
      exitDist,
      landAt: {
        x: hub.x,
        y: hub.y,
        angle:
          exitEdgeIndex >= 0
            ? getTurntablePortWorldAngleDeg(el, anim.toPort)
            : getTurntablePortWorldAngleDeg(el, anim.fromPort),
      },
    });
  }

  for (const [junctionId, anim] of junctionTurntableStore.getAll()) {
    if (!anim.boarded) continue;
    const el = byId[junctionId];
    if (!el) continue;
    const pose = getTurntableRidePose(el, anim);
    rotating.push({
      ohtId: anim.ohtId,
      junctionId,
      x: pose.x,
      y: pose.y,
      angle: pose.angle,
    });
    holdAtHubOhtIds.add(anim.ohtId);
  }

  return { completed, rotating, holdAtHubOhtIds };
}

/**
 * Hold the OHT at the hub when the disk still has to turn for this move.
 * Straight-through traffic on an already-aligned disk passes without stopping.
 */
export function shouldBlockTurntableHubHop(
  graph: TrackGraph,
  elements: Record<string, LayoutElement>,
  fromEdgeIndex: number,
  toEdgeIndex: number
): boolean {
  const from = graph.edges[fromEdgeIndex];
  const to = graph.edges[toEdgeIndex];
  if (
    !from ||
    !to ||
    from.pathKind !== 'turntable' ||
    to.pathKind !== 'turntable' ||
    from.id !== to.id ||
    from.portIndex == null ||
    to.portIndex == null ||
    fromEdgeIndex === toEdgeIndex
  ) {
    return false;
  }
  const el = elements[from.id];
  if (!el || !isTurntableAnimationEnabled(el)) return false;

  const plan = planTurntableMove(
    el,
    from.portIndex,
    to.portIndex,
    Object.values(elements)
  );
  if (
    Math.abs(plan.deltaDeg) < 0.5 &&
    turntableAnglesAligned(plan.alignFromDeg, plan.alignToDeg)
  ) {
    return false;
  }
  return true;
}

/**
 * Called when the OHT actually reaches the hub: board the reserved disk, or
 * open a fresh reservation if the approach tick hasn't made one yet.
 * Returns false when another OHT owns the disk, so the caller keeps waiting.
 */
export function ensureTurntableRotationStarted(params: {
  graph: TrackGraph;
  elements: Record<string, LayoutElement>;
  fromEdgeIndex: number;
  toEdgeIndex: number;
  exitDir: 1 | -1;
  ohtId: string;
  now: number;
}): boolean {
  const from = params.graph.edges[params.fromEdgeIndex];
  const to = params.graph.edges[params.toEdgeIndex];
  if (!from || !to || from.portIndex == null || to.portIndex == null) return false;
  if (from.pathKind !== 'turntable' || to.pathKind !== 'turntable' || from.id !== to.id) {
    return false;
  }
  const el = params.elements[from.id];
  if (!el || !isTurntableAnimationEnabled(el)) return false;

  const existing = junctionTurntableStore.getAnim(from.id);
  if (existing) {
    if (existing.ohtId !== params.ohtId) return false;
    if (existing.toPort === to.portIndex) {
      junctionTurntableStore.markBoarded(from.id, params.ohtId);
      return true;
    }
    // Exit changed since the approach reservation — re-plan from the live angle.
    const rails = Object.values(params.elements);
    const plan = planTurntableMove(el, from.portIndex, to.portIndex, rails);
    if (shouldLockTurntableBarForPorts(el, from.portIndex, to.portIndex, rails)) {
      return false;
    }
    junctionTurntableStore.start({
      id: from.id,
      ...plan,
      alignFromDeg: junctionTurntableStore.getHubAngle(from.id, plan.alignFromDeg),
      fromPort: from.portIndex,
      toPort: to.portIndex,
      exitEdgeIndex: params.toEdgeIndex,
      exitDir: params.exitDir,
      ohtId: params.ohtId,
      boarded: true,
      now: params.now,
      force: true,
    });
    return true;
  }

  if (junctionTurntableStore.isCoolingDown(from.id, params.now)) return false;
  if (junctionTurntableStore.isOhtCoolingDown(params.ohtId, params.now)) return false;

  const rails = Object.values(params.elements);
  const plan = planTurntableMove(el, from.portIndex, to.portIndex, rails);
  if (shouldLockTurntableBarForPorts(el, from.portIndex, to.portIndex, rails)) {
    return false;
  }
  junctionTurntableStore.start({
    id: from.id,
    ...plan,
    fromPort: from.portIndex,
    toPort: to.portIndex,
    exitEdgeIndex: params.toEdgeIndex,
    exitDir: params.exitDir,
    ohtId: params.ohtId,
    boarded: true,
    now: params.now,
  });
  return true;
}

/**
 * True when the intended exit track has no other OHT occupying it.
 * Used by junction traffic control to hold vehicles until the exit clears.
 */
export function isDestinationTrackClear(params: {
  graph: TrackGraph;
  exitEdgeIndex: number;
  requestingOhtId: string;
  ohts: Array<{
    id: string;
    edgeIndex: number;
    dist: number;
    dir: 1 | -1;
  }>;
}): boolean {
  const { graph, exitEdgeIndex, requestingOhtId, ohts } = params;
  const exitEdge = graph.edges[exitEdgeIndex];
  if (!exitEdge) return true;

  for (const other of ohts) {
    if (other.id === requestingOhtId) continue;
    if (other.edgeIndex === exitEdgeIndex) return false;

    if (exitEdge.pathKind !== 'turntable') {
      const otherEdge = graph.edges[other.edgeIndex];
      if (otherEdge && otherEdge.id === exitEdge.id) return false;
    }
  }

  return true;
}

/**
 * How far out from the hub an OHT starts steering the disk, as a multiple of
 * the longest spoke. Gives the bar time to swing before the vehicle arrives.
 */
const ENGAGE_RADIUS_FACTOR = 1.6;
/** Inside this fraction of the ring radius the OHT is held for a real turn. */
const HOLD_RADIUS_FACTOR = 0.55;
/** After a turn finishes, ignore the same OHT until it clears this multiple of the ring. */
const RELEASE_RADIUS_FACTOR = 1.25;

/** World distance from the hub within which an OHT drives the disk. */
export function getTurntableEngageRadius(el: LayoutElement): number {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const spokes = getCircleJunctionSpokesLocal(w, h, getCircleJunctionMaxPorts(el));
  const cx = w / 2;
  const cy = h / 2;
  let longestSpoke = 0;
  for (const s of spokes) {
    longestSpoke = Math.max(longestSpoke, Math.hypot(s.tipX - cx, s.tipY - cy));
  }
  const fallback = getCircleJunctionRingRadius(w, h);
  return Math.max(longestSpoke, fallback) * ENGAGE_RADIUS_FACTOR;
}

/** World distance from the hub at which a turning OHT is held for the disk ride. */
export function getTurntableHoldRadius(el: LayoutElement): number {
  return getCircleJunctionRingRadius(el.dimensions.width, el.dimensions.height) * HOLD_RADIUS_FACTOR;
}

export type PlaybackTurntableOht = {
  id: string;
  x: number;
  y: number;
  angle: number;
  /** Heading a short time ahead — used to detect entry→exit turns. */
  lookaheadAngle?: number;
  lookaheadX?: number;
  lookaheadY?: number;
};

/**
 * Port whose axis best matches the way an OHT is travelling. The diameter bar
 * is undirected, so a spoke counts as matching whether the vehicle runs along
 * it or against it.
 */
export function resolveTurntableTargetPort(
  el: LayoutElement,
  headingWorldDeg: number,
  activePorts?: ReadonlySet<number>,
  /** When set, break ties by preferring the port opposite the entry (straight-through). */
  entryPort?: number
): number | null {
  const spokes = getCircleJunctionSpokesLocal(
    el.dimensions.width,
    el.dimensions.height,
    getCircleJunctionMaxPorts(el)
  );
  const localHeading = headingWorldDeg - (el.rotation || 0);
  const restrict = activePorts && activePorts.size > 0 ? activePorts : null;

  let bestIndex: number | null = null;
  let bestError = Infinity;
  spokes.forEach((spoke, index) => {
    if (restrict && !restrict.has(index)) return;
    const d = Math.abs(shortestAngleDeltaDeg(localHeading, spoke.angle));
    const error = Math.min(d, Math.abs(180 - d));
    if (error < bestError - 1e-9) {
      bestError = error;
      bestIndex = index;
    } else if (
      entryPort != null &&
      error <= bestError + 1e-9 &&
      bestIndex === entryPort &&
      index !== entryPort
    ) {
      bestIndex = index;
    }
  });
  return bestIndex;
}

export function resolveTurntableExitPort(
  el: LayoutElement,
  headingWorldDeg: number,
  entryPort: number,
  activePorts: ReadonlySet<number> | undefined,
  rails: readonly LayoutElement[] = []
): number {
  const opposite = resolveTurntableOppositePort(el, entryPort, activePorts);
  const resolved =
    resolveTurntableTargetPort(el, headingWorldDeg, activePorts, entryPort) ??
    opposite ??
    entryPort;
  if (
    opposite != null &&
    shouldLockTurntableBarForPorts(el, entryPort, opposite, rails)
  ) {
    return opposite;
  }
  return resolved;
}

export type TurntableAlignUpdate = {
  id: string;
  updates: Partial<LayoutElement>;
};

export type TurntableAlignTickResult = {
  updates: TurntableAlignUpdate[];
  /** OHT poses pinned to the hub while waiting / riding a turn. */
  holding: TurntablePose[];
};

export type AuthoritativeJunctionSegmentPortMap = ReadonlyMap<string, number>;

/**
 * Resolve backend segment ids to the junction port represented by that
 * exported graph edge. The compound key prevents repeated rail ids at
 * different junctions from becoming ambiguous.
 */
export function buildAuthoritativeJunctionSegmentPortMap(
  elements: LayoutElement[]
): AuthoritativeJunctionSegmentPortMap {
  const graph = buildTrackGraph(elements);
  const result = new Map<string, number>();
  for (const { edge, segmentId } of getExportableTrackEdges(graph, elements)) {
    if (edge.pathKind !== 'turntable' || edge.portIndex == null) continue;
    result.set(`${edge.id}\u0000${segmentId}`, edge.portIndex);
  }
  return result;
}

type TurntableAlignmentBaseParams = {
  /** Simulation clock time in milliseconds. */
  now: number;
  elements: Record<string, LayoutElement> | LayoutElement[];
  ohts: PlaybackTurntableOht[];
};

export type TurntableAlignmentTickParams = TurntableAlignmentBaseParams &
  (
    | {
        /**
         * Legacy browser inference. This is intentionally opt-in so a
         * versioned backend stream can never fall through to proximity logic.
         */
        mode: 'legacy-proximity';
        prevOhts?: Array<{ id: string; x: number; y: number }> | null;
        /**
         * Junction timing already published by the stream. These OHTs may still
         * pre-align a disk while rolling, but must never be held here: their
         * dwell is a leg on the timeline, so a browser hold would stack a
         * second wait on top of it. Their junctions are driven elsewhere.
         */
        streamTimed?: {
          ohtIds: ReadonlySet<string>;
          junctionIds: ReadonlySet<string>;
        } | null;
      }
    | {
        /** Deterministic replay driven only by ordered backend events. */
        mode: 'authoritative-events';
        visualizationEvents: readonly VisualizationEvent[];
        segmentPortMap?: AuthoritativeJunctionSegmentPortMap;
      }
  );

const AUTHORITATIVE_JUNCTION_PHASES = new Set<VisualizationEvent['phase']>([
  'junction_enter',
  'junction_align',
  'junction_rotate',
  'junction_clear',
]);

/**
 * Whether a replay stream can drive circle-junction disks from events.
 * Compiled legacy plans always exist now, but they usually omit
 * fromSegmentId/toSegmentId — those must fall back to proximity or the
 * turntable never rotates.
 */
export function hasAuthoritativeJunctionKinematics(
  events: readonly VisualizationEvent[] | null | undefined
): boolean {
  if (!events?.length) return false;
  return events.some(
    (event) =>
      (event.kind === 'JUNCTION' || event.kind === 'SWITCH') &&
      AUTHORITATIVE_JUNCTION_PHASES.has(event.phase) &&
      Boolean(event.junctionId || event.connectorId) &&
      Boolean(event.fromSegmentId) &&
      Boolean(event.toSegmentId)
  );
}

function eventProgress(event: VisualizationEvent, nowS: number): number {
  if (event.endT == null || event.endT <= event.t) return nowS >= event.t ? 1 : 0;
  return Math.max(0, Math.min(1, (nowS - event.t) / (event.endT - event.t)));
}

function smoothstep01(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

function localPointToWorld(
  el: LayoutElement,
  local: { x: number; y: number }
): { x: number; y: number } {
  const radians = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: el.position.x + local.x * cos - local.y * sin,
    y: el.position.y + local.x * sin + local.y * cos,
  };
}

function authoritativePortForSegment(
  junctionId: string,
  segmentId: string | undefined,
  portMap: AuthoritativeJunctionSegmentPortMap
): number | null {
  if (!segmentId) return null;
  return portMap.get(`${junctionId}\u0000${segmentId}`) ?? null;
}

function authoritativeGroupKey(event: VisualizationEvent): string {
  return [
    event.junctionId ?? event.connectorId ?? '',
    event.operationId,
    event.ohtId ?? '',
  ].join('\u0000');
}

/**
 * Replay junction/switch motion from the backend contract.
 *
 * ENTER captures the OHT at the incoming ring; ALIGN moves only the disk;
 * ROTATE boards the OHT at the hub and turns it toward the outgoing segment.
 * Once captured, the OHT remains held at the last deterministic pose until
 * CLEAR occurs. No vehicle proximity, lookahead, wait property, or local
 * animation completion can release it.
 */
export function tickAuthoritativeTurntableEvents(params: {
  now: number;
  elements: Record<string, LayoutElement> | LayoutElement[];
  ohts: PlaybackTurntableOht[];
  visualizationEvents: readonly VisualizationEvent[];
  segmentPortMap?: AuthoritativeJunctionSegmentPortMap;
}): TurntableAlignTickResult {
  const list = Array.isArray(params.elements)
    ? params.elements
    : Object.values(params.elements);
  const byId = Array.isArray(params.elements)
    ? (Object.fromEntries(list.map((element) => [element.id, element])) as Record<
        string,
        LayoutElement
      >)
    : params.elements;
  const portMap =
    params.segmentPortMap ?? buildAuthoritativeJunctionSegmentPortMap(list);
  const nowS = params.now / 1000;
  const groups = new Map<string, VisualizationEvent[]>();

  for (const event of params.visualizationEvents) {
    if (
      (event.kind !== 'JUNCTION' && event.kind !== 'SWITCH') ||
      !AUTHORITATIVE_JUNCTION_PHASES.has(event.phase)
    ) {
      continue;
    }
    const key = authoritativeGroupKey(event);
    const group = groups.get(key);
    if (group) group.push(event);
    else groups.set(key, [event]);
  }

  const updates: TurntableAlignUpdate[] = [];
  const holding: TurntablePose[] = [];

  for (const events of groups.values()) {
    events.sort((a, b) => a.sequence - b.sequence);
    const first = events.find((event) => event.phase !== 'junction_clear');
    if (!first || first.t > nowS || !first.ohtId) continue;
    const junctionId = first.junctionId ?? first.connectorId;
    if (!junctionId) continue;
    const el = byId[junctionId];
    if (!el) continue;

    const clear = events.find(
      (event) => event.phase === 'junction_clear' && event.t <= nowS
    );
    const railType = resolveRailType(el);
    if (railType !== 'junction-circle') {
      // H-connectors/switches use the same explicit ENTER/ROTATE/CLEAR hold
      // contract. Their mechanical geometry remains static, but the boarded
      // OHT is pinned to the connector until the backend CLEAR edge.
      if (clear) continue;
      const center = localPointToWorld(el, {
        x: el.dimensions.width / 2,
        y: el.dimensions.height / 2,
      });
      const vehicle = params.ohts.find((oht) => oht.id === first.ohtId);
      holding.push({
        ohtId: first.ohtId,
        junctionId,
        x: center.x,
        y: center.y,
        angle: vehicle?.angle ?? el.rotation ?? 0,
      });
      continue;
    }

    const incomingSegment = events.find((event) => event.fromSegmentId)?.fromSegmentId;
    const outgoingSegment = events.find((event) => event.toSegmentId)?.toSegmentId;
    const fromPort = authoritativePortForSegment(junctionId, incomingSegment, portMap);
    const toPort = authoritativePortForSegment(junctionId, outgoingSegment, portMap);
    if (fromPort == null || toPort == null) continue;

    // Use canonical port axes rather than the previously rendered Redux angle.
    // This keeps random seeks and frame-by-frame playback bit-for-bit equivalent.
    const baseBarAngle = getTurntablePortAngleDeg(el, 0);
    const entryBarAngle = getTurntablePortAngleDeg(el, fromPort);
    const headingInDeg = entryBarAngle + 180;
    const headingDeltaDeg = shortestAngleDeltaDeg(
      headingInDeg,
      getTurntablePortAngleDeg(el, toPort)
    );
    const exitBarAngle = entryBarAngle + headingDeltaDeg;
    const align = events.find((event) => event.phase === 'junction_align');
    const rotate = events.find((event) => event.phase === 'junction_rotate');
    const lockBar = shouldLockTurntableBarForPorts(el, fromPort, toPort, list);
    let diskAngle = lockBar
      ? getConfiguredTurntableBarAngleDeg(el, list)
      : baseBarAngle;

    if (!lockBar) {
      if (align && nowS >= align.t) {
        diskAngle =
          baseBarAngle +
          shortestAngleDeltaDeg(baseBarAngle, entryBarAngle) *
            smoothstep01(eventProgress(align, nowS));
      }
      if (rotate && nowS >= rotate.t) {
        diskAngle =
          entryBarAngle +
          shortestAngleDeltaDeg(entryBarAngle, exitBarAngle) *
            smoothstep01(eventProgress(rotate, nowS));
      }
    }

    if (
      !lockBar &&
      !turntableAnglesAligned(getTurntableSettledAngleDeg(el, list), diskAngle, 0.1)
    ) {
      updates.push({
        id: el.id,
        updates: {
          customProperties: {
            ...el.customProperties,
            turntableAngleDeg: diskAngle,
          },
        },
      });
    }

    if (clear) continue;
    if (lockBar) continue;

    const rotateStarted = Boolean(rotate && nowS >= rotate.t);
    const headingProgress =
      rotateStarted && rotate ? smoothstep01(eventProgress(rotate, nowS)) : 0;
    if (rotateStarted) {
      const hub = getTurntableHubWorld(el);
      holding.push({
        ohtId: first.ohtId,
        junctionId,
        x: hub.x,
        y: hub.y,
        angle: headingInDeg + headingDeltaDeg * headingProgress + (el.rotation || 0),
      });
    } else {
      const spoke = getCircleJunctionSpokesLocal(
        el.dimensions.width,
        el.dimensions.height,
        getCircleJunctionMaxPorts(el)
      )[fromPort];
      if (!spoke) continue;
      const approach = localPointToWorld(el, { x: spoke.ringX, y: spoke.ringY });
      holding.push({
        ohtId: first.ohtId,
        junctionId,
        x: approach.x,
        y: approach.y,
        angle: headingInDeg + (el.rotation || 0),
      });
    }
  }

  return { updates, holding };
}

/** Resolve the exit port from a short lookahead sample (bearing from hub preferred). */
function resolvePlaybackExitPort(
  el: LayoutElement,
  hub: { x: number; y: number },
  oht: PlaybackTurntableOht,
  activePorts: ReadonlySet<number> | undefined,
  entryPort: number,
  rails: readonly LayoutElement[] = []
): number {
  if (
    oht.lookaheadX != null &&
    oht.lookaheadY != null &&
    Number.isFinite(oht.lookaheadX) &&
    Number.isFinite(oht.lookaheadY)
  ) {
    const dx = oht.lookaheadX - hub.x;
    const dy = oht.lookaheadY - hub.y;
    if (Math.hypot(dx, dy) > 1) {
      const bearing = (Math.atan2(dy, dx) * 180) / Math.PI;
      return resolveTurntableExitPort(el, bearing, entryPort, activePorts, rails);
    }
  }
  const ahead = oht.lookaheadAngle ?? oht.angle;
  return resolveTurntableExitPort(el, ahead, entryPort, activePorts, rails);
}

/** Shortest distance from point P to segment AB. */
function distPointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const len2 = abx * abx + aby * aby;
  if (len2 <= 1e-12) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * abx + (py - ay) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t));
}

/** Parametric t of point P projected onto segment AB, clamped to [0, 1]. */
function projectTOnSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const len2 = abx * abx + aby * aby;
  if (len2 <= 1e-12) return 0;
  const t = ((px - ax) * abx + (py - ay) * aby) / len2;
  return Math.max(0, Math.min(1, t));
}

/**
 * True when this frame should board the turntable hold.
 *
 * - Inside the hold disk → capture.
 * - High-speed skip (prev→curr chord through the hub) → capture only while the
 *   OHT is still inside the engage zone and the hub lies between the samples.
 * - Departing / already past the junction → never re-board (prevents teleport
 *   snaps back to the hub after the vehicle has left).
 */
export function ohtCrossesTurntableHold(params: {
  hub: { x: number; y: number };
  holdRadius: number;
  oht: PlaybackTurntableOht;
  prev?: { x: number; y: number } | null;
  /** Max distance from hub for skip-capture; defaults to 2.5× holdRadius. */
  engageRadius?: number;
}): boolean {
  const { hub, holdRadius, oht, prev } = params;
  const engageRadius = params.engageRadius ?? holdRadius * 2.5;
  const currDist = Math.hypot(oht.x - hub.x, oht.y - hub.y);
  if (currDist <= holdRadius) return true;
  if (!prev) return false;

  // Already cleared the junction footprint — never yank the OHT back.
  if (currDist > engageRadius) return false;

  const chordHits =
    distPointToSegment(hub.x, hub.y, prev.x, prev.y, oht.x, oht.y) <= holdRadius;
  if (!chordHits) return false;

  const prevDist = Math.hypot(prev.x - hub.x, prev.y - hub.y);
  // Departing: outside hold and not getting closer. Only allow a true hub jump
  // (both samples outside hold, hub between them on this frame's segment).
  if (currDist >= prevDist - 1e-6) {
    if (prevDist <= holdRadius) return false;
    const t = projectTOnSegment(hub.x, hub.y, prev.x, prev.y, oht.x, oht.y);
    return t > 0.05 && t < 0.95;
  }

  // Still approaching through the chord.
  return true;
}

/**
 * Map each OHT to at most one circle junction — the closest hub within engage
 * range. Prevents a nearby busy/idle junction from stealing another OHT's approach.
 */
export function assignOhtsToNearestJunctions(params: {
  junctions: LayoutElement[];
  ohts: PlaybackTurntableOht[];
}): Map<string, PlaybackTurntableOht> {
  const { junctions, ohts } = params;
  const assignment = new Map<string, PlaybackTurntableOht>(); // junctionId -> oht
  const claimedOhts = new Set<string>();

  // Skip OHTs already boarded on any disk.
  for (const oht of ohts) {
    if (junctionTurntableStore.isOhtOnTurntable(oht.id)) claimedOhts.add(oht.id);
  }

  type Candidate = { junctionId: string; oht: PlaybackTurntableOht; dist: number };
  const candidates: Candidate[] = [];
  for (const el of junctions) {
    const hub = getTurntableHubWorld(el);
    const engageRadius = getTurntableEngageRadius(el);
    for (const oht of ohts) {
      if (claimedOhts.has(oht.id)) continue;
      if (junctionTurntableStore.getParkedJunction(oht.id) === el.id) continue;
      const d = Math.hypot(oht.x - hub.x, oht.y - hub.y);
      if (d <= engageRadius) {
        candidates.push({ junctionId: el.id, oht, dist: d });
      }
    }
  }

  // Closest pairs first so each OHT locks to its nearest hub.
  candidates.sort((a, b) => a.dist - b.dist);
  for (const c of candidates) {
    if (claimedOhts.has(c.oht.id) || assignment.has(c.junctionId)) continue;
    // Junction already has a boarded anim for someone else.
    const anim = junctionTurntableStore.getAnim(c.junctionId);
    if (anim?.boarded && anim.ohtId !== c.oht.id) continue;
    assignment.set(c.junctionId, c.oht);
    claimedOhts.add(c.oht.id);
  }
  return assignment;
}

/**
 * Backend-playback turntable traffic control.
 *
 * Every OHT that reaches the hub is held until the disk faces the required
 * exit direction (align and/or rotate). Wait length is simulation-ms
 * (`phaseDurationMs` / {@link TURNTABLE_MIN_HOLD_MS}), so wall-clock wait
 * scales with playback speed via the sim clock. Approaching OHTs may
 * cosmetic-align the bar before the hold radius without freezing the vehicle.
 *
 * `now` is simulation time in milliseconds.
 */
export function tickTurntableAlignments(
  params: TurntableAlignmentTickParams
): TurntableAlignTickResult {
  if (params.mode === 'authoritative-events') {
    return tickAuthoritativeTurntableEvents(params);
  }
  const list = Array.isArray(params.elements)
    ? params.elements
    : Object.values(params.elements);
  const byId = Array.isArray(params.elements)
    ? (Object.fromEntries(list.map((e) => [e.id, e])) as Record<string, LayoutElement>)
    : params.elements;

  const updates: TurntableAlignUpdate[] = [];
  const holding: TurntablePose[] = [];
  const prevById = new Map((params.prevOhts ?? []).map((o) => [o.id, o]));

  const persistAngle = (el: LayoutElement, angleDeg: number) => {
    const snapped = isBidirectionalThroughJunction(el, list)
      ? getConfiguredTurntableBarAngleDeg(el, list)
      : snapTurntableAngleToNearestPort(
          el,
          angleDeg,
          getCircleJunctionActivePortIndices(el, list)
        );
    if (
      turntableAnglesAligned(getTurntableSettledAngleDeg(el, list), snapped, 0.5)
    ) {
      return;
    }
    updates.push({
      id: el.id,
      updates: {
        customProperties: {
          ...el.customProperties,
          turntableAngleDeg: snapped,
        },
      },
    });
  };

  const startCosmeticAlign = (
    el: LayoutElement,
    ohtId: string,
    port: number,
    current: number
  ) => {
    if (isBidirectionalThroughJunction(el, list)) {
      const target = getConfiguredTurntableBarAngleDeg(el, list);
      if (turntableAnglesAligned(current, target, 1)) return;
      const anim = junctionTurntableStore.getAnim(el.id);
      if (anim?.boarded) return;
      if (anim && turntableAnglesAligned(anim.alignToDeg, target, 20)) return;
      junctionTurntableStore.start({
        id: el.id,
        phase: 'aligning',
        alignFromDeg: current,
        alignToDeg: target,
        headingInDeg: getTurntablePortAngleDeg(el, port) + 180,
        deltaDeg: 0,
        fromPort: port,
        toPort: port,
        exitEdgeIndex: -1,
        exitDir: 1,
        ohtId,
        boarded: false,
        now: params.now,
        force: true,
      });
      return;
    }
    const portAngle = getTurntablePortAngleDeg(el, port);
    const target =
      Math.abs(shortestAngleDeltaDeg(current, portAngle)) <=
      Math.abs(shortestAngleDeltaDeg(current, portAngle + 180))
        ? portAngle
        : portAngle + 180;
    if (turntableAnglesAligned(current, target, 1)) return;
    const anim = junctionTurntableStore.getAnim(el.id);
    if (anim?.boarded) return;
    // Already targeting this axis, or close enough mid-sweep (hysteresis).
    if (anim && turntableAnglesAligned(anim.alignToDeg, target, 20)) return;
    junctionTurntableStore.start({
      id: el.id,
      phase: 'aligning',
      alignFromDeg: current,
      alignToDeg: target,
      headingInDeg: portAngle + 180,
      deltaDeg: 0,
      fromPort: port,
      toPort: port,
      exitEdgeIndex: -1,
      exitDir: 1,
      ohtId,
      boarded: false,
      now: params.now,
      force: true,
    });
  };

  /**
   * Dedicated wait (`waitSec`) with the disk frozen, then the turntable
   * animation, then release. The OHT does not leave until the rotate finishes.
   */
  const startJunctionHold = (
    el: LayoutElement,
    ohtId: string,
    fromPort: number,
    toPort: number
  ) => {
    // Same-port approach: align the bar to the travel axis only (no 180° U-turn plan).
    const plan =
      fromPort === toPort
        ? {
            alignFromDeg: getTurntableSettledAngleDeg(el),
            alignToDeg: getTurntablePortAngleDeg(el, fromPort),
            headingInDeg: getTurntablePortAngleDeg(el, fromPort) + 180,
            deltaDeg: 0,
          }
        : planTurntableMove(el, fromPort, toPort, list);

    const lockBar = shouldLockTurntableBarForPorts(el, fromPort, toPort, list);
    const settled = getTurntableSettledAngleDeg(el, list);
    const current = junctionTurntableStore.getHubAngle(el.id, settled);
    const configuredBar = getConfiguredTurntableBarAngleDeg(el, list);
    const alignCandA = plan.alignToDeg;
    const alignCandB = plan.alignToDeg + 180;
    const entryAlignTo =
      Math.abs(shortestAngleDeltaDeg(current, alignCandA)) <=
      Math.abs(shortestAngleDeltaDeg(current, alignCandB))
        ? alignCandA
        : alignCandB;
    const finalBarDeg = lockBar
      ? configuredBar
      : fromPort === toPort
        ? entryAlignTo
        : plan.alignToDeg + plan.deltaDeg;
    const headingDeltaDeg = fromPort === toPort ? 0 : plan.deltaDeg;
    const waitMs = getJunctionWaitMs(el);
    const rotateMs = Math.max(TURNTABLE_MIN_HOLD_MS, turntableDurationMs(current, finalBarDeg));
    const holdDelaySec = waitMs / 1000 + rotateMs / 1000;

    junctionTurntableStore.claimJunction(el.id, ohtId);
    junctionTurntableStore.start({
      id: el.id,
      phase: waitMs > 0 ? 'waiting' : 'aligning',
      alignFromDeg: current,
      alignToDeg: waitMs > 0 ? current : finalBarDeg,
      headingInDeg: plan.headingInDeg,
      deltaDeg: 0,
      headingDeltaDeg: waitMs > 0 ? 0 : headingDeltaDeg,
      pendingAlignToDeg: finalBarDeg,
      pendingHeadingDeltaDeg: headingDeltaDeg,
      fromPort,
      toPort,
      exitEdgeIndex: -1,
      exitDir: 1,
      ohtId,
      boarded: true,
      now: params.now,
      force: true,
      minHoldMs: waitMs,
      holdDelaySec,
    });
  };

  const junctions = list.filter(
    (el) =>
      resolveRailType(el) === 'junction-circle' && isTurntableAnimationEnabled(el)
  );

  // One OHT → one junction (closest hub). Stops a busy neighbor from stealing traffic.
  const assigned = assignOhtsToNearestJunctions({
    junctions,
    ohts: params.ohts.filter((o) => !junctionTurntableStore.isOhtCoolingDown(o.id, params.now)),
  });

  for (const el of junctions) {
    // A disk turning on a streamed dwell is owned by that dwell until it ends.
    if (params.streamTimed?.junctionIds.has(el.id)) continue;
    const hub = getTurntableHubWorld(el);
    const holdRadius = getTurntableHoldRadius(el);
    const releaseRadius =
      getCircleJunctionRingRadius(el.dimensions.width, el.dimensions.height) *
      RELEASE_RADIUS_FACTOR;
    const activePorts = getCircleJunctionActivePortIndices(el, list);
    const anim = junctionTurntableStore.getAnim(el.id);

    // Heal bars already stuck off-axis (e.g. frozen mid-sweep from an older build).
    if (!anim) {
      const settled = getTurntableSettledAngleDeg(el, list);
      if (isBidirectionalThroughJunction(el, list)) {
        const configured = getConfiguredTurntableBarAngleDeg(el, list);
        if (!turntableAnglesAligned(settled, configured, 0.5)) {
          persistAngle(el, configured);
        }
      } else {
        const snapped = snapTurntableAngleToNearestPort(el, settled, activePorts);
        if (!turntableAnglesAligned(settled, snapped, 0.5)) {
          persistAngle(el, snapped);
        }
      }
    }

    // Active boarded ride: keep holding until the disk finishes — do not cancel
    // just because playback already walked past the hub.
    if (anim?.boarded) {
      continue;
    }

    // Release parked lock once the OHT has cleared the junction.
    for (const oht of params.ohts) {
      const parkedOn = junctionTurntableStore.getParkedJunction(oht.id);
      if (parkedOn !== el.id) continue;
      const away = Math.hypot(oht.x - hub.x, oht.y - hub.y);
      if (away > releaseRadius) junctionTurntableStore.releaseParked(oht.id);
    }

    const nearest = assigned.get(el.id) ?? null;

    if (!nearest) {
      // Nobody in range: do NOT freeze a mid-sweep cosmetic angle (that left the
      // bar stuck on a diagonal). Let the in-flight align finish via tick below;
      // only clear if there is no anim at all.
      continue;
    }

    if (junctionTurntableStore.isCoolingDown(el.id, params.now)) continue;

    const entryPort = resolveTurntableTargetPort(el, nearest.angle, activePorts);
    if (entryPort == null) continue;
    const exitPort = resolvePlaybackExitPort(
      el,
      hub,
      nearest,
      activePorts,
      entryPort,
      list
    );
    if (isTurntableStraightThroughMove(el, entryPort, exitPort, list)) {
      continue;
    }
    const settled = getTurntableSettledAngleDeg(el);
    const current = junctionTurntableStore.getHubAngle(el.id, settled);
    const nearestDist = Math.hypot(nearest.x - hub.x, nearest.y - hub.y);
    const engageRadius = getTurntableEngageRadius(el);
    const shouldHold = ohtCrossesTurntableHold({
      hub,
      holdRadius,
      engageRadius,
      oht: nearest,
      prev: prevById.get(nearest.id) ?? null,
    });

    // Still approaching: cosmetic pre-align only (vehicle keeps moving).
    if (!shouldHold) {
      if (nearestDist <= engageRadius) {
        startCosmeticAlign(el, nearest.id, entryPort, current);
      }
      continue;
    }

    // At / through the hub — hold while the disk builds the exit direction.
    const existing = junctionTurntableStore.getAnim(el.id);
    if (existing?.boarded && existing.ohtId === nearest.id) {
      // Already holding / riding.
    } else if (existing && existing.ohtId !== nearest.id && existing.boarded) {
      // Another OHT owns the disk — keep waiting outside.
    } else if (junctionTurntableStore.getParkedJunction(nearest.id) === el.id) {
      // Just finished this junction — do not re-board until parked is released.
    } else if (params.streamTimed?.ohtIds.has(nearest.id)) {
      // The stream schedules this vehicle's junction dwells. Let it roll: its
      // wait leg already stops it, and the disk turns inside that window.
    } else {
      startJunctionHold(el, nearest.id, entryPort, exitPort);
    }
  }

  for (const id of junctionTurntableStore.tick(params.now, phaseDurationMs)) {
    const anim = junctionTurntableStore.getAnim(id);
    const el = byId[id];
    if (!anim || !el) {
      junctionTurntableStore.clear(id);
      continue;
    }

    if (anim.boarded) {
      const finalAngle = shouldLockTurntableBarForPorts(
        el,
        anim.fromPort,
        anim.toPort,
        list
      )
        ? getConfiguredTurntableBarAngleDeg(el, list)
        : junctionTurntableStore.getFinalAngle(anim);
      persistAngle(el, finalAngle);
      // Shift this OHT's playback clock by the wait so it resumes at the hub
      // instead of jumping to wherever the global timeline advanced.
      playbackDelayStore.addDelay(
        anim.ohtId,
        anim.holdDelaySec ?? getJunctionWaitSec(el)
      );
      junctionTurntableStore.markSettled(id, params.now);
      continue;
    }

    // Cosmetic entry align finished — snap to target port axis and clear.
    persistAngle(el, anim.alignToDeg);
    junctionTurntableStore.clear(id);
  }

  for (const [junctionId, anim] of junctionTurntableStore.getAll()) {
    if (!anim.boarded) continue;
    const el = byId[junctionId];
    if (!el) continue;
    const pose = getTurntableRidePose(el, anim);
    holding.push({
      ohtId: anim.ohtId,
      junctionId,
      x: pose.x,
      y: pose.y,
      angle: pose.angle,
    });
  }

  return { updates, holding };
}

/**
 * Pre-align the turntable diameter bar to the incoming port before the OHT
 * drives onto the hub. Uses the `aligning` phase with `deltaDeg = 0` so the
 * store settles without a boarded hub ride.
 */
export function ensureEntryRotationStarted(params: {
  junctionId: string;
  portIndex: number;
  ohtId: string;
  elements: Record<string, LayoutElement> | LayoutElement[];
  now: number;
}): boolean {
  const list = Array.isArray(params.elements)
    ? params.elements
    : Object.values(params.elements);
  const el = list.find((e) => e.id === params.junctionId);
  if (!el) return false;
  if (!isTurntableAnimationEnabled(el)) return false;

  if (junctionTurntableStore.isAnimating(params.junctionId)) return true;

  const settled = getTurntableSettledAngleDeg(el);
  const currentAngle = junctionTurntableStore.getHubAngle(params.junctionId, settled);
  const entryAngle = getTurntablePortAngleDeg(el, params.portIndex);

  if (turntableAnglesAligned(currentAngle, entryAngle)) return false;

  const candA = entryAngle;
  const candB = entryAngle + 180;
  const alignToDeg =
    Math.abs(shortestAngleDeltaDeg(currentAngle, candA)) <=
    Math.abs(shortestAngleDeltaDeg(currentAngle, candB))
      ? candA
      : candB;

  junctionTurntableStore.start({
    id: params.junctionId,
    phase: 'aligning',
    alignFromDeg: currentAngle,
    alignToDeg,
    headingInDeg: entryAngle + 180,
    deltaDeg: 0,
    fromPort: params.portIndex,
    toPort: params.portIndex,
    exitEdgeIndex: -1,
    exitDir: 1,
    ohtId: params.ohtId,
    boarded: false,
    now: params.now,
    force: true,
  });
  return true;
}
